<template>
  <main
    role="main"
    class="col content-spacing"
    style="max-width: 88% !important"
  >
    <section>
      <PageHeader pageTitle="Usuários" style="padding-top: 1rem">
        <div style="display: flex; gap: 0.5rem">
          <GenericSearchInput v-model="search.textSearch" />
          <SelectProfile v-model="search.profile" />
          <SelectAccountManager v-model="search.accountManagerId" />
          <BaseButtonComponent
            @click="getAll()"
            variant="primary-action"
            :disabled="!userList.length"
          >
            Buscar
          </BaseButtonComponent>
          <VueJsonToCsv block :json-data="CSVUserList" :separator="';'">
            <BaseButtonComponent
              block
              variant="primary-action"
              :disabled="!CSVUserList.length"
            >
              CSV
            </BaseButtonComponent>
          </VueJsonToCsv>
        </div>
      </PageHeader>
    </section>

    <b-skeleton-table
      animation="wave"
      style="margin-top: 2rem"
      :rows="5"
      :columns="15"
      :table-props="{ bordered: true, striped: true }"
      v-if="isLoading"
    />

    <section class="d-flex justify-content-between flex-column">
      <Table
        style="margin-top: 1rem"
        class="user-table"
        v-if="!isLoading && userList.length !== 0"
      >
        <TableHeader>
          <TableRow>
            <TableHead
              v-for="(userCol, index) of userColumns"
              :key="`${userCol}_${index}`"
            >
              <ColumnHeader
                :sort="userCol.sort"
                :somatory="userCol.somatory"
                :columnName="userCol.name"
                :columnKey="userCol.key"
                :columnType="userCol.columnType"
                :currentColumnFiltered="currentColumnFiltered"
                :sortDirection="sortDirection"
                :dataList="userList"
                v-on:sortListByKey="sortListByKey(userCol.key)"
              />
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          <TableRow v-for="(user, index) of userList" :key="`${user}_${index}`">
            <TableCell>
              <RenderSocialNumberColumnValue
                :socialNumber="user.socialNumber"
              />
            </TableCell>
            <TableCell>
              {{ user.name }}
            </TableCell>
            <TableCell>
              <RenderPhoneColumnValue :phoneNumber="user.phone" />
            </TableCell>
            <TableCell>
              {{ user.email }}
            </TableCell>
            <TableCell>
              <b-form-input
                v-bind="false"
                v-model="user.nameCoupon"
                style="width: 9rem"
              />
            </TableCell>
            <TableCell>
              {{ user.state }}
            </TableCell>
            <TableCell>
              {{ user.zipCode }}
            </TableCell>
            <TableCell>
              {{ user.city }}
            </TableCell>
            <TableCell>
              {{ user.district }}
            </TableCell>
            <TableCell>
              {{ user.address }}
            </TableCell>
            <TableCell
              style="min-width: 200px"
              v-if="accountManagerOptions.length"
            >
              <SelectAccountManager
                :options-data="accountManagerOptions"
                v-model="user.accountManagerId"
                @input="setAccountManager(user)"
              />
            </TableCell>
            <TableCell>
              {{ dateWithHourFormat(user.createdAt) }}
            </TableCell>
            <TableCell>
              {{ user.lastApiAccess }}
            </TableCell>
            <TableCell>
              <b-form-checkbox
                :checked="Boolean(user.hasUcAvailable)"
                @change="updateHasUcAvailable(user)"
              />
            </TableCell>
            <TableCell>
              <b-form-checkbox
                :checked="Boolean(user.consumer)"
                :disabled="true"
              />
            </TableCell>
            <TableCell>
              <b-form-checkbox
                :checked="Boolean(user.investor)"
                :disabled="true"
              />
            </TableCell>
            <TableCell>
              <b-form-checkbox
                :checked="Boolean(user.trader)"
                :disabled="true"
              />
            </TableCell>
            <TableCell>
              <BaseButtonComponent
                size="sm"
                @click="changeCouponName(user.id, user.nameCoupon)"
                variant="primary-action"
                :disabled="!user.nameCoupon"
              >
                {{ user.nameCoupon ? 'Alterar Cupom' : 'Adicionar Cupom' }}
              </BaseButtonComponent>

              <BaseButtonComponent
                size="sm"
                variant="primary-action"
                @click="openUserDetailsDialog(user)"
              >
                Editar
              </BaseButtonComponent>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
      <empty-list v-else-if="!isLoading" />
      <AlphabetPagination @letter-selected="fetchDataForLetter" />
    </section>
    <EditUserDetailsDialog ref="editUserDetailsModalRef" @getAll="getAll" />
  </main>
</template>

<script>
import VueJsonToCsv from 'vue-json-to-csv';
import { PageHeader } from '../../ui/page-header';
import { SelectAccountManager, SelectProfile } from '../../ui/selects';
import { Api } from '../../vue/api';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../ui/table';
import { ColumnHeader } from '../../ui/column-header';
import {
  RenderPhoneColumnValue,
  RenderSocialNumberColumnValue,
} from '../../ui/render';
import {
  formatSocialNumber,
  dateWithoutHourFormat,
  formatPhoneNumber,
  dateWithHourFormat,
} from '../../utils';
import RenderDateColumnValue from '../../ui/render/render-date-column-value.vue';
import { GenericSearchInput } from '../../ui/inputs';
import helpers from '../../vue/helpers';
import { EditUserDetailsDialog } from '../../modules/user/components';
import { BaseButtonComponent } from '../../ui/buttons';
import AlphabetPagination from '../../ui/pagination/alphabet-pagination.vue';
import { ref } from 'vue';
const API = new Api();

/**
 * Objeto que representa os usuários para a construção da tabela
 * @typedef {Object} User
 * @property {number} id
 * @property {string} socialNumber
 * @property {string} name
 * @property {string} phone
 * @property {string} email
 * @property {string} nameCoupon
 * @property {string} state
 * @property {string} zipCode
 * @property {string} city
 * @property {string} district
 * @property {string} address
 * @property {string} createdAt
 * @property {string} lastApiAccess
 * @property {boolean} hasUcAvailable
 * @property {boolean} consumer
 * @property {boolean} investor
 * @property {boolean} trader
 * @property {string} number
 * @property {number} accountManagerId
 */

/**
 * Objeto de usuários mapeados para o formato CSV
 * @typedef {Object} CSVUser
 * @property {string} Nome
 * @property {string} CPF_CNPJ
 * @property {string} Telefone
 * @property {string} Email
 * @property {string} Cupom
 * @property {string} Estado
 * @property {string} Cidade
 * @property {string} Bairro
 * @property {string} Endereco
 * @property {string} Data_cadastro
 * @property {string} Ultimo_acesso
 * @property {boolean} Crm
 * @property {boolean} Consumidor
 * @property {boolean} Investidor
 * @property {boolean} Comercializador
 * @property {string} Ativo
 * @property {string} CEP
 */

export default {
  components: {
    PageHeader,
    SelectProfile,
    Table,
    VueJsonToCsv,
    TableHeader,
    TableBody,
    TableRow,
    TableHead,
    ColumnHeader,
    TableCell,
    RenderPhoneColumnValue,
    RenderDateColumnValue,
    RenderSocialNumberColumnValue,
    GenericSearchInput,
    EditUserDetailsDialog,
    SelectAccountManager,
    BaseButtonComponent,
    AlphabetPagination,
  },
  data() {
    return {
      search: {
        profile: null,
        textSearch: '',
        page: 1,
        accountManagerId: null,
        letter: 'A',
      },
      currentColumnFiltered: '',
      sortDirection: 'ASC',
      isLoading: false,
      accountManagerOptions: [],
      /**
       * Lista de usuários com dados preservados da consulta a API
       * @type {User[]}
       */
      userList: [],
      CSVUserList: [],
      userColumns: [
        {
          name: 'CPF/CNPJ',
          key: 'socialNumber',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Nome',
          key: 'name',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Telefone',
          key: 'phone',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Email',
          key: 'email',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Cupom',
          key: 'nameCoupon',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'UF',
          key: 'state',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'CEP',
          key: 'zipCode',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Cidade',
          key: 'city',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Bairro',
          key: 'district',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Endereço',
          key: 'address',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'CS',
          key: 'accountManagerId',
          columnType: '',
          sort: true,
          somatory: false,
        },
        {
          name: 'Data Cadastro ',
          key: 'createdAt',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Ultimo Acesso',
          key: 'lastApiAccess',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'CRM',
          key: 'hasUcAvailable',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Consumidor',
          key: 'consumer',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Investidor',
          key: 'investor',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Promotor',
          key: 'trader',
          columnType: '',
          sort: false,
          somatory: false,
        },
        {
          name: 'Opção',
          key: '',
          columnType: '',
          sort: false,
          somatory: false,
        },
      ],
    };
  },
  setup() {
        const editUserDetailsModalRef = ref(null);
    return{
      editUserDetailsModalRef
    }
  },
  async mounted() {
    const accountOptions = localStorage.getItem('accountManagerOptions');
    this.accountManagerOptions = JSON.parse(accountOptions);
    this.$router.push({
      query: {
        page: this.$route.query.page || 1,
        search: this.search.textSearch,
        profile: this.search.profile,
        accountManagerId: this.search.accountManagerId,
        letter: this.search.letter,
      },
    });
    await this.getAll();
  },
  computed: {
    profile() {
      return this.search.profile;
    },
    textSearch() {
      return this.search.textSearch;
    },
    accountManagerId() {
      return this.search.accountManagerId;
    },
  },
  watch: {
    profile() {
      this.handleFilterUserList();
    },
    textSearch() {
      this.handleFilterUserList();
    },
    accountManagerId() {
      this.handleFilterUserList();
    },
  },
  methods: {
    dateWithHourFormat,
    async fetchDataForLetter(letter) {
      this.search.letter = letter;
      this.handleFilterUserList();
    },
    /**
     * @return {function(): void}
     */
    async getAll() {
      this.isLoading = true;
      const { ok, errors, model } = await API.post('/execute', {
        path: '/users' + helpers.toQueryString(this.$route.query),
        method: 'get',
      });

      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }

      this.count = model.count;
      this.search.page = model.page;
      this.search.pageCount = model.pageCount;
      const userList = model.list.map(user => ({
        id: user.id,
        socialNumber: user.socialNumber,
        name: user.name,
        phone: user.phone,
        email: user.email,
        nameCoupon: user.couponName,
        state: user.state,
        zipCode: user.zipCode,
        city: user.city,
        district: user.district,
        address: `${user.address} ${user.number}`,
        createdAt: user.createdAt,
        lastApiAccess: user.lastApiAccessString,
        hasUcAvailable: Boolean(user.hasUcAvailable),
        consumer: user.profiles.length
          ? user.profiles.includes('consumer')
          : false,
        investor: user.profiles.length
          ? user.profiles.includes('investor')
          : false,
        trader: user.profiles.length ? user.profiles.includes('trader') : false,
        number: user.number,
        accountManagerId: user.accountManagerId,
      }));
      this.userList = userList;
      this.CSVUserList = this.parseUserListToCSVUserList(userList);
      this.isLoading = false;
    },
    /**
     * Método para mapear os dados de usuario para formato CSV
     * @param {User[]} userList
     * @return {function(): CSVUser[]}
     */
    parseUserListToCSVUserList(userList) {
      return userList.map(user => ({
        Nome: user.name,
        Cidade: user.city,
        Estado: user.state,
        Bairro: user.district,
        Endereco: `${user.address} ${user.number}`,
        CEP: user.zipCode,
        CPF_CNPJ: formatSocialNumber(user.socialNumber),
        Consumidor: user.consumer ? '✅' : '',
        Investidor: user.investor ? '✅' : '',
        Comercializador: user.trader ? '✅' : '',
        Telefone: user.phone ? formatPhoneNumber(user.phone.trim()) : '',
        Email: user.email,
        Cupom: user.couponName ? user.couponName : '',
        Ativo: user.activationStatus,
        Crm: user.hasUcAvailable ? 'Ativo' : 'Inativo',
        Data_cadastro: dateWithHourFormat(user.createdAt),
        Ultimo_acesso: dateWithHourFormat(user.lastApiAccess),
      }));
    },
    /**
     * Altera o nome de um cupom de usuário.
     * @async
     * @param {number} userId - O objeto representando o cupom a ser alterado.
     * @param {string} couponName - O novo nome para o cupom.
     * @throws {Error} Se ocorrer um erro durante a alteração do cupom.
     */
    async changeCouponName(userId, couponName) {
      this.isLoading = true;
      const { ok, errors } = await API.post('/execute', {
        path: `/user/coupon/change/${userId}/`,
        method: 'post',
        data: { couponName },
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      this.$bvToast.toast('Cupom alterado com sucesso', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      this.isLoading = false;
    },
    /**
     * Adiciona um gerente de contas ao CPF/CNPJ
     * @async
     * @param {User} user - ID da conta do usuário responsável por esse CNF/CNPJ.
     * @throws {Error} Se ocorrer um erro durante a adição de uma responsável da conta.
     */
    async setAccountManager(user) {
      this.isLoading = true;
      const { ok, errors } = await API.post('/execute', {
        path: `/user/account-manager/${user.id}/`,
        method: 'post',
        data: { accountManagerId: parseInt(user.accountManagerId, 10) },
      });
      if (!ok) {
        this.$bvToast.toast(errors.message, {
          title: `Erro`,
          variant: 'error',
          solid: true,
        });
        this.isLoading = false;
        return;
      }
      this.$bvToast.toast('Gerente de conta adicionada com sucesso', {
        title: `Sucesso`,
        variant: 'success',
        solid: true,
      });
      this.isLoading = false;
    },
    /**
     * Método para filtrar a lista de usuários
     * @return {function(): void}
     */
    async handleFilterUserList() {
      this.$route.query.profile = this.search.profile;
      this.$route.query.search = this.search.textSearch;
      this.$route.query.accountManagerId = this.search.accountManagerId;
      this.$route.query.letter = this.search.letter;
      await this.getAll();
    },
    /**
     * Abre um modal e preenche o formulário com os dados do usuário selecionado
     *
     * @param {User} user - Objeto contendo os dados do usuário
     */
    openUserDetailsDialog(user) {
      this.$refs.editUserDetailsModalRef.openModal({
        id: user.id,
        phone: user.phone.slice(2),
        email: user.email,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.user-table {
  display: block;
  max-height: 88vh !important;
  height: auto !important;

  th:first-child,
  td:first-child {
    position: sticky;
    left: 0;
    background-color: #e7e7e7;
  }

  td:nth-child(2),
  th:nth-child(2) {
    position: sticky;
    left: 7rem;
    background-color: #e7e7e7;
  }

  tr:nth-child(odd) td:nth-child(-n + 2) {
    background-color: #e7e7e7;
  }

  tr:nth-child(even) td:nth-child(-n + 2) {
    background-color: #f2f2f2; 
  }
}
</style>
